import dayjs from 'dayjs';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData,
} from '../dto/data-model.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { OnlineProductOrder } from './online-product-order.js';
import { TimeZone } from '../../time/timezone.js';
import { cloneData, isSameData } from '../dto/data-model-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { toEnumFromString } from '../../serialization/enum-utils.js';

export class OfflineProductOrderCreatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = '239a2b18ad7746cf8053677c8d5e4642';

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly order: OnlineProductOrder,
  ) {
    super();
  }

  get $typeId(): string {
    return OfflineProductOrderCreatedEvent.TYPE_ID;
  }

  static create(args: {
    uniqueId: string,
    creatorId: string | null,
    deviceId: string | null,
    creationDate: dayjs.Dayjs,
    source: SyncEventSource,
    order: OnlineProductOrder,
  }): OfflineProductOrderCreatedEvent {
    return new OfflineProductOrderCreatedEvent(
        args.uniqueId,
        args.creatorId,
        args.deviceId,
        args.creationDate,
        args.source,
        args.order
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OfflineProductOrderCreatedEvent {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      creatorId: MapUtils.getStringOrNull(map, 'creatorId'),
      deviceId: MapUtils.getStringOrNull(map, 'deviceId'),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      source: toEnumFromString(MapUtils.getString(map, 'source'), SyncEventSource),
      order: context.fromData<OnlineProductOrder>(MapUtils.getRecord(map, 'order')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      uniqueId: this.uniqueId,
      creatorId: this.creatorId,
      deviceId: this.deviceId,
      creationDate: context.encodeDate(this.creationDate),
      source: this.source,
      order: context.toData(this.order),
    };
  }

  clone(): this {
    return OfflineProductOrderCreatedEvent.create({
      uniqueId: this.uniqueId,
      creatorId: this.creatorId,
      deviceId: this.deviceId,
      creationDate: TimeZone.cloneDate(this.creationDate),
      source: this.source,
      order: cloneData(this.order),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
           other.creatorId === this.creatorId &&
           other.deviceId === this.deviceId &&
           other.creationDate === this.creationDate &&
           other.source === this.source &&
           isSameData(other.order, this.order);
  }

  static verifyType(model: DataModel): boolean {
    return model instanceof OfflineProductOrderCreatedEvent;
  }

  static assertType(model: DataModel): model is OfflineProductOrderCreatedEvent {
    return model instanceof OfflineProductOrderCreatedEvent;
  }
}

export class OfflineProductOrderCreatedEventBuilder extends DataModelBuilder<OfflineProductOrderCreatedEvent> {
  build(context: SerializationContext, map: SerializedData): OfflineProductOrderCreatedEvent {
    return OfflineProductOrderCreatedEvent.fromMap(context, map);
  }
}
